import * as React from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import moment from "moment";
import { Form, Dropdown } from "react-bootstrap";
import TempRatingModal from "../../ratings/components/TempRatingModal";

export interface Job {
  id: number;
  position_type: string;
  temp_filled: boolean;
  closed: boolean;
  published: boolean;
  boosted?: boolean;
  full_service?: boolean;
  rpo: boolean;
  rpo_redacted: boolean;
  job_type_id: number;
  practice_id: number;
  position_type_id: number;
  job_title?: string;
  temp_date?: string;
  start_date?: string;
  created_at: string;
  published_at?: string;
  job_applications: JobApplication[];
  pending_rating?: {
    rating_id: number;
    needs_rating: boolean;
    rating_value?: number;
  } | null;
  job_type: {
    id: number;
    title: string;
    group: string;
  };
  practice: {
    id: number;
    name: string;
  };
}

interface JobApplication {
  hidden: boolean;
  seen: boolean;
}

export interface PositionType {
  shortname: string;
  name: string;
}

export interface JobType {
  id: number;
  title: string;
  group: string;
}

export interface Practice {
  id: number;
  name?: string;
}

const useIsPhone = () => {
  const [isPhone, setIsPhone] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 767 : false
  );

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const update = () => setIsPhone(mediaQuery.matches);
    update();
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);

  return isPhone;
};

const positionType = ({
  row,
  position_types,
}: {
  row: Job;
  position_types: PositionType[];
}): string => {
  const pt = position_types.find((jt) => jt.shortname === row.position_type);
  return pt ? pt.name : "";
};

const tempType = (row: Job): boolean =>
  row.position_type === "temp" || row.position_type === "longtermtemp";

// Timezone-safe check if temp job date has passed
const isTempDatePast = (row: Job): boolean => {
  if (!tempType(row) || !row.temp_date) return false;
  const dateParts = row.temp_date.toString().split('T')[0].split('-');
  const tempDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return tempDate < today;
};

const jobStatus = (row: Job): string => {
  if (row.temp_filled) return "Accepted";
  if (row.closed) return "Closed";
  // Check if temp job date has passed - show as Expired
  if (tempType(row) && isTempDatePast(row)) return "Closed";
  if (tempType(row) && row.published) return "Open";
  if (row.published) return "Published";
  return "Draft";
};

const jobTypeTitle = (row: Job, jobTypes: JobType[]): string =>
  row.job_title || row.job_type?.title || jobTypes.find((type) => type.id === row.job_type_id)?.title || 'Untitled job';

const jobDateLabel = (row: Job): string => {
  if (row.position_type === 'longtermtemp') {
    return row.start_date ? moment(row.start_date).format('ll') : 'Start date not set';
  }
  if (tempType(row)) {
    return row.temp_date ? moment(row.temp_date).format('ll') : 'Date not set';
  }
  return moment(row.created_at).format('ll');
};

// Check if job is a permanent position type (for expiration tracking)
const isPermJob = (row: Job): boolean => {
  return ['fulltime', 'parttime', 'longtermtemp'].includes(row.position_type);
};

// Calculate days remaining until job expires (30 days from published_at)
// Boosted and full_service jobs don't auto-expire — they stay open until manually closed
const getDaysRemaining = (row: Job): number | null => {
  if (!row.published || row.closed || !isPermJob(row) || !row.published_at) return null;
  if (row.boosted || row.full_service) return null;
  const publishedAt = moment(row.published_at);
  const expiresAt = publishedAt.clone().add(30, 'days');
  return expiresAt.diff(moment(), 'days');
};

const jobActions = (row: Job) => {
  const url = `/jobs/${row.id}`;
  const clone_url = `/jobs/${row.id}/clone`;
  const edit_url = `/jobs/${row.id}/edit`;

  return (
    <div className="d-flex justify-content-center" data-testid="job-actions">
      <Dropdown>
        <Dropdown.Toggle
          variant="primary"
          size="sm"
          className="job-actions-dropdown"
          style={{
            fontSize: '0.875rem',
            padding: '0.5rem 0.875rem',
            borderRadius: '0.5rem',
            border: 'none',
            color: 'white',
            background: 'linear-gradient(135deg, #085ec8, #176fc9)',
            transition: 'all 0.3s ease',
            fontWeight: 600,
            boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            lineHeight: '1'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
          }}
        >
          <i className="fa fa-ellipsis-h"></i>
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu align="end" className="context-menu">
          {!row.published && !row.closed && (
            <Dropdown.Item
              className="context-menu__item context-menu__item--success"
              href={url}
              data-testid={`publish-job-${row.id}`}
            >
              <i className="fa fa-book" aria-hidden="true"></i>
              Publish
            </Dropdown.Item>
          )}
          <Dropdown.Item className="context-menu__item" href={clone_url}>
            <i className="fa fa-copy" aria-hidden="true"></i>
            Clone
          </Dropdown.Item>
          {!row.closed && (
            <React.Fragment>
              <Dropdown.Item
                className="context-menu__item"
                href={edit_url}
                target="_blank"
                data-testid={`edit-job-${row.id}`}
              >
                <i className="fa fa-pencil" aria-hidden="true"></i>
                Edit
              </Dropdown.Item>
              <Dropdown.Divider />
              {row.position_type === 'temp' ? (
                <Dropdown.Item
                  as="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to close this temp job?')) {
                      const form = document.createElement('form');
                      form.method = 'POST';
                      form.action = '/job_closing_surveys/skip';

                      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                      if (csrfToken) {
                        const csrfInput = document.createElement('input');
                        csrfInput.type = 'hidden';
                        csrfInput.name = 'authenticity_token';
                        csrfInput.value = csrfToken;
                        form.appendChild(csrfInput);
                      }

                      const jobIdInput = document.createElement('input');
                      jobIdInput.type = 'hidden';
                      jobIdInput.name = 'job_id';
                      jobIdInput.value = row.id.toString();
                      form.appendChild(jobIdInput);

                      document.body.appendChild(form);
                      form.submit();
                    }
                  }}
                  type="button"
                  className="context-menu__item context-menu__item--danger"
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                  Close Job
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  as="button"
                  onClick={(e) => {
                    e.preventDefault();

                    // Get the actual dropdown button for anchor positioning
                    const dropdownButton = (e.currentTarget as HTMLElement)
                      .closest('.dropdown')
                      ?.querySelector('.dropdown-toggle') as HTMLElement;

                    // Create a synthetic element with the job close data attributes
                    // The JobClosingSurveyIntegration component listens for clicks on [data-job-close="true"]
                    const syntheticButton = document.createElement('button');
                    syntheticButton.setAttribute('data-job-close', 'true');
                    syntheticButton.setAttribute('data-job-id', row.id.toString());
                    syntheticButton.setAttribute('data-job-title', row.job_title || row.job_type?.title || '');

                    // Store reference to real dropdown button for positioning the quick close menu
                    if (dropdownButton) {
                      // Ensure dropdown button has an ID
                      if (!dropdownButton.id) {
                        dropdownButton.id = `dropdown-anchor-${row.id}-${Date.now()}`;
                      }
                      // Store the ID so JobClosingSurveyIntegration can find the real anchor
                      syntheticButton.setAttribute('data-anchor-element', dropdownButton.id);
                    }

                    syntheticButton.style.display = 'none';
                    document.body.appendChild(syntheticButton);

                    // Trigger a click event that will bubble to document level
                    syntheticButton.click();

                    // Clean up
                    setTimeout(() => {
                      document.body.removeChild(syntheticButton);
                    }, 100);
                  }}
                  type="button"
                  className="context-menu__item context-menu__item--danger"
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                  Close Job
                </Dropdown.Item>
              )}
            </React.Fragment>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

function titleField({
  row,
  job_types,
}: {
  row: Job;
  job_types: JobType[];
}): React.ReactNode {
  const styles: React.CSSProperties = {
    color: '#085ec8',
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'color 0.3s ease'
  };
  // Use either the job_type object from API or find it in job_types array
  const jobTypeTitle = row.job_type?.title ||
    job_types.find((jt) => jt.id === row.job_type_id)?.title || "";
  const url = `/jobs/${row.id}`;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <a
        href={url}
        style={styles}
        className="text-decoration-none"
        onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
        onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
      >
        {jobTypeTitle}
      </a>
      {row.boosted && <span className="badge-modern badge-modern--warning ms-1" title="Boosted">B</span>}
      {row.full_service && <span className="badge-modern badge-modern--info ms-1" title="Full Service">FS</span>}
      {row.rpo && <span className="badge-modern badge-modern--primary ms-1" title="Recruitment Process Outsourcing">RPO</span>}
    </span>
  );
}

const dataColumns = ({
  position_types,
  job_types,
  practices,
  temp,
  onOpenRatingModal,
}: {
  position_types: PositionType[];
  job_types: JobType[];
  practices: Practice[];
  temp: boolean;
  onOpenRatingModal: (ratingId: number) => void;
}): TableColumn<Job>[] => [
    {
      id: 'job_type_id',
      name: "Job Title",
      selector: (row) => row.job_type_id,
      sortable: true,
      cell: (row) => titleField({ row, job_types }),
      grow: 2,
    },
    {
      id: 'practice_id',
      name: "Location",
      selector: (row) => row.practice_id,
      sortable: true,
      cell: (row) => {
        // First try practice from row, then find in practices array using either practice.id or practice_id
        const practice = row.practice || practices.find(p => p.id === (row.practice?.id || row.practice_id));
        const practiceName = practice?.name || "Unknown Practice";
        return (
          <a
            href={`/practices/${practice?.id}`}
            style={{
              color: '#085ec8',
              fontWeight: 700,
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            className="text-decoration-none"
            onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
            onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
          >
            {practiceName}
          </a>
        );
      },
      grow: 1.5,
    },
    {
      id: 'position_type_id',
      name: "Position",
      selector: (row) => row.position_type_id,
      sortable: true,
      cell: (row) => positionType({ row, position_types }),
      grow: 1,
    },
    {
      id: 'createdAt',
      name: temp ? "Job Date" : "Created",
      selector: (row) => {
        // Sort longtermtemp by published_at, temp by temp_date, perm by created_at
        if (row.position_type === "longtermtemp") {
          return row.published_at ? new Date(row.published_at).getTime() : 0;
        }
        if (tempType(row) && row.temp_date) {
          return new Date(row.temp_date).getTime();
        }
        return new Date(row.created_at).getTime();
      },
      sortable: true,
      cell: (row) => {
        // Long term temp: show published date with optional start date
        if (row.position_type === "longtermtemp") {
          const publishedDate = row.published_at
            ? moment(row.published_at).format("ll")
            : "Not published";
          const startDate = row.start_date
            ? moment(row.start_date).format("ll")
            : null;
          return (
            <div style={{ lineHeight: 1.4 }}>
              <div>{publishedDate}</div>
              {startDate && (
                <div className="text-muted small">Start: {startDate}</div>
              )}
            </div>
          );
        }
        // Regular temp jobs: show temp_date
        if (tempType(row)) {
          if (!row.temp_date) {
            return <span className="text-muted">No date</span>;
          }
          const t = moment(row.temp_date).format("ll");
          if (t === "Invalid date") {
            return <span className="text-muted">No date</span>;
          }
          return t;
        }
        // Permanent jobs: show created_at
        return moment(row.created_at).format("ll");
      },
      grow: 0.8,
    },
    {
      name: "Status",
      selector: (row) => {
        if (row.temp_filled) return 3;
        if (row.closed) return 2;
        if (row.published) return 0;
        return 1;
      },
      sortable: true,
      cell: (row) => {
        // Show blue "Rated" button if rating is completed
        if (row.pending_rating && !row.pending_rating.needs_rating && row.pending_rating.rating_value) {
          const solidStars = '★'.repeat(row.pending_rating.rating_value);
          const outlinedStars = '☆'.repeat(5 - row.pending_rating.rating_value);
          const stars = solidStars + outlinedStars;
          return (
            <button
              type="button"
              onClick={() => onOpenRatingModal(row.pending_rating!.rating_id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                color: 'white',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(8, 94, 200, 0.25)',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.35)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(8, 94, 200, 0.25)';
              }}
            >
              <span>{stars}</span>
            </button>
          );
        }

        // Show animated "Rate Me" button if there's a pending rating
        if (row.pending_rating?.needs_rating) {
          return (
            <button
              type="button"
              onClick={() => onOpenRatingModal(row.pending_rating!.rating_id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #ffc107, #ffb300)',
                color: 'white',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(255, 193, 7, 0.25)',
                transition: 'all 0.3s ease',
                animation: 'pulse-scale 2s ease-in-out infinite',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.35)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 193, 7, 0.25)';
              }}
            >
              <i className="fa fa-star"></i>
              <span>Rate Me</span>
            </button>
          );
        }

        // Default status badge
        const status = jobStatus(row);
        let badgeClass = 'badge-modern';

        if (status === 'Published' || status === 'Open') {
          badgeClass += ' badge-modern--success';
        } else if (status === 'Draft') {
          badgeClass += ' badge-modern--info';
        } else if (status === 'Closed') {
          badgeClass += ' badge-modern--light';
        } else if (status === 'Expired') {
          badgeClass += ' badge-modern--secondary';
        } else if (status === 'Accepted') {
          badgeClass += ' badge-modern--primary';
        }

        // For published perm jobs, show days remaining if within 15 days of expiration
        const daysRemaining = getDaysRemaining(row);
        if (daysRemaining !== null && status === 'Published' && daysRemaining <= 15) {
          let expiringBadgeClass = 'badge-modern';
          let expiringText = '';

          if (daysRemaining < 0) {
            expiringBadgeClass += ' badge-modern--secondary';
            expiringText = 'Expired';
          } else if (daysRemaining === 0) {
            expiringBadgeClass += ' badge-modern--danger';
            expiringText = 'Expires today';
          } else if (daysRemaining <= 5) {
            expiringBadgeClass += ' badge-modern--danger';
            expiringText = `${daysRemaining}d left`;
          } else {
            expiringBadgeClass += ' badge-modern--warning';
            expiringText = `${daysRemaining}d left`;
          }

          return <span className={expiringBadgeClass} style={{ whiteSpace: 'nowrap' }}>{expiringText}</span>;
        }

        return <span className={badgeClass} style={{ whiteSpace: 'nowrap' }}>{status}</span>;
      },
      minWidth: '100px',
      grow: 1,
    },
    {
      name: "Applicants",
      sortable: true,
      selector: (row) => {
        if (row.rpo_redacted) return 0;
        const apps = row.job_applications.filter((ja) => !ja.hidden);
        const unseen = apps.filter((ja) => !ja.seen).length;
        return unseen > 0 ? -1000000 + unseen : -unseen; // Show New App(s) first, then sort by number of new applicants
      },
      cell: (row) => {
        if (row.rpo_redacted) {
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem 1rem',
                background: '#e9ecef',
                color: '#495057',
                borderRadius: '50px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
              title="Applicant details for this RPO job are managed by your recruiter"
            >
              Managed by recruiter
            </span>
          );
        }

        const apps = row.job_applications.filter((ja) => !ja.hidden);
        const seen = apps.filter((ja) => ja.seen).length;
        const unseen = apps.filter((ja) => !ja.seen).length;
        const bgColor = temp ? '#fe6718' : '#085ec8';
        const bgColorHover = temp ? '#ff8c47' : '#176fc9';

        if (unseen === 0) {
          return (
            <a
              href={`/jobs/${row.id}#${row.position_type === 'temp' ? 'matched' : 'applicants'}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                background: `linear-gradient(135deg, ${bgColor}, ${bgColorHover})`,
                color: 'white',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                textDecoration: 'none',
                boxShadow: `0 2px 8px rgba(0, 0, 0, 0.15)`,
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
              className="text-decoration-none"
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 4px 12px rgba(0, 0, 0, 0.2)`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 2px 8px rgba(0, 0, 0, 0.15)`;
              }}
            >
              <span style={{ fontWeight: 700 }}>{seen}</span>
              <span>Applicants</span>
            </a>
          );
        }

        return (
          <a
            href={`/jobs/${row.id}#${row.position_type === 'temp' ? 'matched' : 'applicants'}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: '0.5rem 1rem',
              background: `linear-gradient(135deg, #71ad08, #8bc34a)`,
              color: 'white',
              borderRadius: '50px',
              fontSize: '0.875rem',
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(113, 173, 8, 0.25)',
              transition: 'all 0.3s ease',
              animation: 'pulse-scale 2s ease-in-out infinite',
              whiteSpace: 'nowrap'
            }}
            className="text-decoration-none"
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(113, 173, 8, 0.35)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(113, 173, 8, 0.25)';
            }}
          >
            <i className="fa fa-bell"></i>
            <span style={{ fontWeight: 700 }}>{unseen}</span>
            <span>New App(s)</span>
          </a>
        );
      },
      grow: 1.2,
    },
    {
      name: "Actions",
      sortable: false,
      cell: (row) => jobActions(row),
      grow: 0.8,
    },
  ];

function MobileJobCard({
  job,
  jobTypes,
  positionTypes,
  temp,
  onOpenRatingModal,
}: {
  job: Job;
  jobTypes: JobType[];
  positionTypes: PositionType[];
  temp: boolean;
  onOpenRatingModal: (ratingId: number) => void;
}) {
  const visibleApplications = job.job_applications.filter((application) => !application.hidden);
  const unseenApplications = visibleApplications.filter((application) => !application.seen).length;
  const status = jobStatus(job);
  const statusClass = status === 'Published' || status === 'Open'
    ? 'badge-modern--success'
    : status === 'Draft'
      ? 'badge-modern--info'
      : status === 'Accepted'
        ? 'badge-modern--primary'
        : 'badge-modern--light';
  const applicantHash = job.position_type === 'temp' ? 'matched' : 'applicants';

  return (
    <article className="mobile-job-card" data-testid={`mobile-job-card-${job.id}`}>
      <div className={`mobile-job-card__accent${temp ? ' mobile-job-card__accent--temp' : ''}`}></div>
      <div className="mobile-job-card__body">
        <div className="mobile-job-card__header">
          <div>
            <a className="mobile-job-card__title" href={`/jobs/${job.id}`}>
              {jobTypeTitle(job, jobTypes)}
            </a>
            <a className="mobile-job-card__practice" href={`/practices/${job.practice?.id || job.practice_id}`}>
              <i className="fa fa-map-marker me-1" aria-hidden="true"></i>
              {job.practice?.name || 'Unknown practice'}
            </a>
          </div>
          <span className={`badge-modern ${statusClass}`}>{status}</span>
        </div>

        <div className="mobile-job-card__meta">
          <div className="mobile-job-card__meta-item">
            Position
            <strong>{positionType({ row: job, position_types: positionTypes }) || 'Not set'}</strong>
          </div>
          <div className="mobile-job-card__meta-item">
            {temp ? 'Job date' : 'Created'}
            <strong>{jobDateLabel(job)}</strong>
          </div>
        </div>

        {job.pending_rating?.needs_rating && (
          <button
            type="button"
            className="management-workspace__action mb-3"
            onClick={() => onOpenRatingModal(job.pending_rating!.rating_id)}
          >
            <i className="fa fa-star" aria-hidden="true"></i>
            Rate this professional
          </button>
        )}

        <div className="mobile-job-card__footer">
          {job.rpo_redacted ? (
            <span className="mobile-job-card__applicants">Managed by recruiter</span>
          ) : (
            <a
              href={`/jobs/${job.id}#${applicantHash}`}
              className={`mobile-job-card__applicants${unseenApplications ? ' mobile-job-card__applicants--new' : ''}`}
            >
              <i className={`fa ${unseenApplications ? 'fa-bell' : 'fa-users'}`} aria-hidden="true"></i>
              {unseenApplications
                ? `${unseenApplications} new applicant${unseenApplications === 1 ? '' : 's'}`
                : `${visibleApplications.length} applicant${visibleApplications.length === 1 ? '' : 's'}`}
            </a>
          )}
          {jobActions(job)}
        </div>
      </div>
    </article>
  );
}

interface ServerFilterState {
  jobTitleFilter: string;
  practiceFilter: string;
  statusFilter: string;
}

interface ServerSortState {
  column: string;
  direction: 'asc' | 'desc';
}

interface ManageJobListProps {
  jobs: Job[];
  position_types: PositionType[];
  job_types: JobType[];
  temp: boolean;
  practices: Practice[];
  paginationTotalRows?: number;
  paginationPerPage?: number;
  paginationDefaultPage?: number;
  onChangePage?: (page: number) => void;
  onChangeRowsPerPage?: (currentRowsPerPage: number, currentPage: number) => void;
  serverFilters?: ServerFilterState;
  onServerFiltersChange?: (filters: ServerFilterState) => void;
  serverSort?: ServerSortState;
  onServerSortChange?: (column: string, direction: 'asc' | 'desc') => void;
  loading?: boolean;
}

// Add custom hook for handling click outside and keyboard navigation
const useClickOutsideAndKeyboard = (
  ref: React.RefObject<HTMLDivElement>,
  onClose: () => void,
  onSelect: (index: number) => void,
  suggestions: string[],
  currentIndex: number,
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
) => {
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!suggestions.length) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setCurrentIndex(prevIndex =>
            Math.min(prevIndex + 1, suggestions.length - 1)
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setCurrentIndex(prevIndex => Math.max(0, prevIndex - 1));
          break;
        case "Enter":
          event.preventDefault();
          if (currentIndex >= 0) {
            onSelect(currentIndex);
          }
          break;
        case "Escape":
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [ref, onClose, onSelect, suggestions, currentIndex, setCurrentIndex]);
};

const DROPDOWN_STYLES = {
  container: {
    position: "absolute" as const,
    width: "100%",
    zIndex: 1000,
    backgroundColor: "#fafafa",
    border: "1px solid #dee2e6",
    borderRadius: "0.25rem",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    marginTop: "2px",
    maxHeight: "200px",
    overflowY: "auto" as const
  },
  item: (isSelected: boolean) => ({
    cursor: "pointer",
    backgroundColor: isSelected ? "#176fc9" : "transparent",
    color: isSelected ? "white" : "inherit",
    transition: "all 0.15s ease"
  })
};

interface FilterDropdownProps<T> {
  items: T[];
  value: string;
  onChange: (value: string) => void;
  getDisplayValue: (item: T) => string;
  placeholder: string;
  show: boolean;
  onClose: () => void;
  selectedIndex: number;
  onIndexChange: (index: number) => void;
}

function FilterDropdown<T>({
  items,
  value,
  onChange,
  getDisplayValue,
  placeholder,
  show,
  onClose,
  selectedIndex,
  onIndexChange
}: FilterDropdownProps<T>) {
  const ref = React.useRef<HTMLDivElement>(null);

  useClickOutsideAndKeyboard(
    ref,
    onClose,
    (index: number) => {
      onChange(getDisplayValue(items[index]));
      onClose();
    },
    items.map(getDisplayValue),
    selectedIndex,
    onIndexChange
  );

  return (
    <div className="management-filter-bar__field" style={{ position: "relative" }} ref={ref}>
      <Form.Group style={{ marginBottom: 0 }}>
        <div className="position-relative">
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{
              marginBottom: 0,
              borderRadius: '0.5rem',
              border: '1px solid #e8ecef',
              padding: '0.5rem 0.75rem',
              fontSize: '0.95rem',
              transition: 'all 0.3s ease'
            }}
          />
          {value && (
            <div
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                zIndex: 5,
                color: '#6c757d'
              }}
              onClick={() => onChange("")}
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </div>
          )}
        </div>
        {items.length > 0 && value && show && (
          <div style={DROPDOWN_STYLES.container}>
            {items.map((item, index) => (
              <div
                key={index}
                onClick={() => {
                  onChange(getDisplayValue(item));
                  onClose();
                }}
                onMouseEnter={() => onIndexChange(index)}
                onMouseLeave={() => onIndexChange(-1)}
                className="px-3 py-2 dropdown-item"
                style={DROPDOWN_STYLES.item(index === selectedIndex)}
              >
                {getDisplayValue(item)}
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    </div>
  );
}

interface FilterBarProps {
  filters: {
    jobTitleFilter: string;
    practiceFilter: string;
    statusFilter: string;
    jobTitleSuggestions: string[];
    practiceSuggestions: string[];
  };
  onFiltersChange: (filters: any) => void;
  job_types: JobType[];
  practices: Practice[];
}

function FilterBar({
  filters,
  onFiltersChange,
  job_types,
  practices
}: FilterBarProps) {
  const [showJobTitleDropdown, setShowJobTitleDropdown] = React.useState(false);
  const [showPracticeDropdown, setShowPracticeDropdown] = React.useState(false);
  const [jobTitleIndex, setJobTitleIndex] = React.useState(-1);
  const [practiceIndex, setPracticeIndex] = React.useState(-1);
  const isPhone = useIsPhone();
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const handleJobTitleFilter = (value: string) => {
    onFiltersChange({
      ...filters,
      jobTitleFilter: value,
      jobTitleSuggestions: value ? job_types
        .filter(jt => jt.title.toLowerCase().includes(value.toLowerCase()))
        .map(jt => jt.title) : []
    });
    if (value) {
      setShowJobTitleDropdown(true);
    }
  };

  const handlePracticeFilter = (value: string) => {
    onFiltersChange({
      ...filters,
      practiceFilter: value,
      practiceSuggestions: value ? practices
        .filter(p => p.name.toLowerCase().includes(value.toLowerCase()))
        .map(p => p.name) : []
    });
    if (value) {
      setShowPracticeDropdown(true);
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      ...filters,
      jobTitleFilter: "",
      practiceFilter: "",
      statusFilter: "",
      jobTitleSuggestions: [],
      practiceSuggestions: []
    });
  };

  // Check if any filters are active
  const hasActiveFilters = filters.jobTitleFilter || filters.practiceFilter || filters.statusFilter;
  const activeFilterCount = [filters.jobTitleFilter, filters.practiceFilter, filters.statusFilter].filter(Boolean).length;
  const showControls = !isPhone || filtersOpen || Boolean(hasActiveFilters);

  return (
    <div className="management-filter-bar">
      {isPhone && (
        <button
          type="button"
          className="management-filter-bar__toggle"
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={showControls}
        >
          <span><i className="fa fa-filter me-2" aria-hidden="true"></i>Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}</span>
          <i className={`fa fa-chevron-${showControls ? 'up' : 'down'}`} aria-hidden="true"></i>
        </button>
      )}
        <div className={`management-filter-bar__controls${showControls ? '' : ' management-filter-bar__controls--collapsed'}`}>
          <FilterDropdown
            items={filters.jobTitleSuggestions}
            value={filters.jobTitleFilter}
            onChange={handleJobTitleFilter}
            getDisplayValue={(title) => title}
            placeholder="Filter by Job Title"
            show={showJobTitleDropdown}
            onClose={() => setShowJobTitleDropdown(false)}
            selectedIndex={jobTitleIndex}
            onIndexChange={setJobTitleIndex}
          />
          <FilterDropdown
            items={filters.practiceSuggestions}
            value={filters.practiceFilter}
            onChange={handlePracticeFilter}
            getDisplayValue={(name) => name}
            placeholder="Filter by Practice"
            show={showPracticeDropdown}
            onClose={() => setShowPracticeDropdown(false)}
            selectedIndex={practiceIndex}
            onIndexChange={setPracticeIndex}
          />
          <div className="management-filter-bar__field" style={{ position: "relative" }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Select
                  value={filters.statusFilter}
                  onChange={(e) => onFiltersChange({ ...filters, statusFilter: e.target.value })}
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Closed">Closed</option>
                  <option value="Open">Open</option>
                  <option value="Published">Published</option>
                  <option value="Draft">Draft</option>
                </Form.Select>
              </div>
            </Form.Group>
          </div>
          <button
            type="button"
            className="btn-modern btn-modern--ghost btn-modern--sm"
            onClick={clearAllFilters}
            style={{
              opacity: hasActiveFilters ? 1 : 0.6
            }}
            disabled={!hasActiveFilters}
          >
            <i className="fa fa-times"></i> Clear filters
          </button>
      </div>
    </div>
  );
}

export default function ManageJobList({
  jobs,
  position_types,
  job_types,
  temp,
  practices,
  paginationTotalRows,
  paginationPerPage,
  paginationDefaultPage,
  onChangePage,
  onChangeRowsPerPage,
  serverFilters,
  onServerFiltersChange,
  serverSort,
  onServerSortChange,
  loading = false,
}: ManageJobListProps) {
  const isPhone = useIsPhone();
  // Use server filters when provided (server-side filtering mode)
  const isServerFiltering = paginationTotalRows !== undefined && onServerFiltersChange;

  const [localFilters, setLocalFilters] = React.useState({
    jobTitleFilter: "",
    practiceFilter: "",
    statusFilter: "",
    jobTitleSuggestions: [] as string[],
    practiceSuggestions: [] as string[]
  });

  // Combined filters state - use serverFilters for main values when server filtering
  const filters = {
    jobTitleFilter: isServerFiltering && serverFilters ? serverFilters.jobTitleFilter : localFilters.jobTitleFilter,
    practiceFilter: isServerFiltering && serverFilters ? serverFilters.practiceFilter : localFilters.practiceFilter,
    statusFilter: isServerFiltering && serverFilters ? serverFilters.statusFilter : localFilters.statusFilter,
    jobTitleSuggestions: localFilters.jobTitleSuggestions,
    practiceSuggestions: localFilters.practiceSuggestions
  };

  // Filter change handler that delegates to server or local state
  const setFilters = (newFilters: typeof localFilters) => {
    // Always update local state for suggestions
    setLocalFilters(newFilters);

    // If server filtering is enabled, also notify parent of filter changes
    if (isServerFiltering && onServerFiltersChange) {
      onServerFiltersChange({
        jobTitleFilter: newFilters.jobTitleFilter,
        practiceFilter: newFilters.practiceFilter,
        statusFilter: newFilters.statusFilter
      });
    }
  };

  // Track current page for pagination display - sync with parent's default page
  const [currentPage, setCurrentPage] = React.useState(paginationDefaultPage || 1);
  const perPage = paginationPerPage || 25;
  const totalPages = paginationTotalRows ? Math.ceil(paginationTotalRows / perPage) : 1;

  // Sync current page with parent when paginationDefaultPage changes
  React.useEffect(() => {
    if (paginationDefaultPage && paginationDefaultPage !== currentPage) {
      setCurrentPage(paginationDefaultPage);
    }
  }, [paginationDefaultPage, currentPage]);

  // Wrap onChangePage to also update local state - memoized to prevent re-render loops
  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
    onChangePage?.(page);
  }, [onChangePage]);

  // Check if server-side sorting is enabled
  const isServerSorting = paginationTotalRows !== undefined && onServerSortChange;

  // Handle sort change - delegate to server if enabled
  const handleSort = React.useCallback((column: any, sortDirection: 'asc' | 'desc') => {
    if (isServerSorting && onServerSortChange) {
      // Map column selector or id to sort column name
      const columnId = column.id || column.selector?.toString() || '';
      onServerSortChange(columnId, sortDirection);
    }
  }, [isServerSorting, onServerSortChange]);

  // Rating modal state
  const [ratingModalOpen, setRatingModalOpen] = React.useState(false);
  const [selectedRatingId, setSelectedRatingId] = React.useState<number | null>(null);

  // Survey modal state removed - now handled by JobClosingSurveyIntegration

  // Rating modal handlers
  const handleOpenRatingModal = (ratingId: number) => {
    setSelectedRatingId(ratingId);
    setRatingModalOpen(true);
  };

  const handleCloseRatingModal = () => {
    setRatingModalOpen(false);
    setSelectedRatingId(null);
  };

  const handleRatingSuccess = () => {
    // Could trigger a refresh here if needed
    // For now, the modal will close automatically after showing success message
  };

  // Survey modal functions removed - now handled by JobClosingSurveyIntegration

  const columns = dataColumns({
    position_types,
    job_types,
    practices,
    temp,
    onOpenRatingModal: handleOpenRatingModal
  });

  const filteredItems = React.useMemo(() => {
    // If pagination is enabled, filtering is done server-side, so return jobs as-is
    if (paginationTotalRows !== undefined) {
      return jobs;
    }

    // Otherwise, apply client-side filtering (backward compatibility)
    return jobs.filter((job) => {
      if (!filters.jobTitleFilter && !filters.practiceFilter && !filters.statusFilter) {
        return true;
      }

      const jobType = job.job_type || job_types.find(jt => jt.id === job.job_type_id);
      const practice = job.practice || practices.find(p => p.id === job.practice_id);

      const matchesJobTitle = !filters.jobTitleFilter ||
        (jobType?.title?.toLowerCase().includes(filters.jobTitleFilter.toLowerCase()) ?? false);

      const matchesPractice = !filters.practiceFilter ||
        (practice?.name?.toLowerCase().includes(filters.practiceFilter.toLowerCase()) ?? false);

      const status = jobStatus(job);
      const matchesStatus = !filters.statusFilter || status === filters.statusFilter;

      return matchesJobTitle && matchesPractice && matchesStatus;
    });
  }, [jobs, filters, job_types, practices, paginationTotalRows]);

  // Mobile cards use a compact pager; the desktop table supplies its own.
  const paginationBar = React.useMemo(() => {
    if (!paginationTotalRows || totalPages <= 1) return null;

    return (
      <nav className="management-pagination" aria-label="Jobs pagination">
        <span>
          Showing {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, paginationTotalRows)} of {paginationTotalRows} jobs
        </span>
        <div className="management-pagination__controls">
          <button
            type="button"
            className="management-pagination__button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <i className="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <strong className="text-center">
            Page {currentPage} of {totalPages}
          </strong>
          <button
            type="button"
            className="management-pagination__button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <i className="fa fa-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </nav>
    );
  }, [currentPage, totalPages, perPage, paginationTotalRows, handlePageChange]);

  return (
    <div style={{ position: 'relative' }}>
      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
        job_types={job_types}
        practices={practices}
      />
      {isPhone && paginationBar}
      {/* Loading overlay */}
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            borderRadius: '0.75rem'
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid #e8ecef',
                borderTop: '4px solid #085ec8',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 0.75rem'
              }}
            />
            <span style={{ color: '#6c757d', fontWeight: 500 }}>Loading...</span>
          </div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
      {isPhone ? (
        filteredItems.length > 0 ? (
          <div className="mobile-job-list" data-testid="mobile-job-list">
            {filteredItems.map((job) => (
              <MobileJobCard
                key={job.id}
                job={job}
                jobTypes={job_types}
                positionTypes={position_types}
                temp={temp}
                onOpenRatingModal={handleOpenRatingModal}
              />
            ))}
          </div>
        ) : (
          <div className="management-empty-state">
            <i className="fa fa-briefcase" aria-hidden="true"></i>
            <h3>No jobs match these filters.</h3>
            <p>Clear the filters or post a new job to get started.</p>
          </div>
        )
      ) : (
      <div className="management-data-surface">
        <DataTable
          data={filteredItems}
          columns={columns}
          defaultSortFieldId={serverSort?.column || "createdAt"}
          defaultSortAsc={serverSort?.direction === 'asc' ? true : false}
          responsive
          sortServer={Boolean(isServerSorting)}
          onSort={isServerSorting ? handleSort : undefined}
          pagination={paginationTotalRows !== undefined}
          paginationServer={paginationTotalRows !== undefined}
          paginationTotalRows={paginationTotalRows}
          paginationPerPage={paginationPerPage || 25}
          paginationDefaultPage={paginationDefaultPage || 1}
          paginationRowsPerPageOptions={[10, 25, 50]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={onChangeRowsPerPage}
          customStyles={{
            responsiveWrapper: {
              style: {
                overflowX: 'visible',
                overflowY: 'visible'
              }
            },
            table: {
              style: {
                width: '100%',
                tableLayout: 'fixed'
              }
            },
            headCells: {
              style: {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#6c757d',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem'
              }
            },
            cells: {
              style: {
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                overflow: 'visible'
              }
            }
          }}
        />
      </div>
      )}

      {/* Job Closing Survey Modal removed - now handled by JobClosingSurveyIntegration */}

      {/* Temp Rating Modal */}
      {selectedRatingId && (
        <TempRatingModal
          ratingId={selectedRatingId}
          show={ratingModalOpen}
          onHide={handleCloseRatingModal}
          onSuccess={handleRatingSuccess}
        />
      )}
    </div>
  );
}
